import { BusinessObjectBase } from './base';
import { studentAssignmentSchema } from '@schemas/business-objects/student-assignment';
import type { SelectedStudent } from './selected-student';
import type { Slot } from './slot';
import type { TermTutor } from './term-tutor';
import type { CalendarDate, Id } from '../base-types';
import type { Student } from './student';
import type { Term } from './term';
import type { TermTime } from './term-time';
import type { Tutor } from './tutor';
import type { User } from './user';

export interface StudentAssignmentData {
  readonly id: Id;
  readonly from?: CalendarDate;
  readonly to?: CalendarDate;
  readonly status?: string;
  readonly selectedStudentId: Id;
  readonly selectedStudent?: SelectedStudent;
  readonly slotId: Id;
  readonly slot?: Slot;
  readonly termTutorId: Id;
  readonly termTutor?: TermTutor;
  readonly replacesId: Id;
  readonly replaces?: StudentAssignment;
  readonly replacedBy?: StudentAssignment;
}

export interface StudentAssignment extends Readonly<StudentAssignmentData> {}
type StudentAssignmentCreateWithChildrenData = Partial<StudentAssignmentData>;

export class StudentAssignment extends BusinessObjectBase {
  static readonly entityName = 'StudentAssignment' as const;
  static readonly plural = 'student-assignments';
  static schema = studentAssignmentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<StudentAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new StudentAssignment(data));
  }

  static async findOne(filter?: any): Promise<StudentAssignment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new StudentAssignment(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<StudentAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new StudentAssignment(data);
  }

  static async create(
    data: Omit<StudentAssignmentData, 'id'>,
  ): Promise<StudentAssignment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new StudentAssignment(result);
  }

  static async createMany(
    data: Omit<StudentAssignmentData, 'id'>[],
  ): Promise<StudentAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new StudentAssignment(item));
  }

  static async createWithChildren(
    data: StudentAssignmentCreateWithChildrenData,
  ): Promise<StudentAssignment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new StudentAssignment(result);
  }

  static async createManyWithChildren(
    data: StudentAssignmentCreateWithChildrenData[],
  ): Promise<StudentAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new StudentAssignment(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<StudentAssignmentData>,
  ): Promise<StudentAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new StudentAssignment(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async checkExistingProposal(
    termTutorId: Id,
    slotId: Id | undefined,
    selectedStudentId: Id,
    replacesId: Id | undefined,
    from?: string,
    to?: string,
  ): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/check-existing-proposal`;
    return this.post<any>(url, {
      termTutorId,
      slotId,
      selectedStudentId,
      replacesId,
      from,
      to,
    });
  }
}
